import type { ParsedObservation, Phase } from "./types.ts";

export const MINUTE = 60_000;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;

type PhaseInput = Pick<ParsedObservation, "endAt" | "sourceState"> | null;

/** Where a lot is in its life, derived only from the latest observation and the clock. */
export function derivePhase(obs: PhaseInput, now: number): Phase {
  if (!obs) return "unknown";
  if (obs.sourceState === "closed" || obs.sourceState === "sold" || obs.sourceState === "unsold") return "closed";
  if (!obs.endAt) return "unknown";
  const left = Date.parse(obs.endAt) - now;
  if (Number.isNaN(left)) return "unknown";
  // Soft-close extensions and live sales run minutes to hours past the scheduled end, never a day.
  // Past that, the lot has closed even if we never saw the site say so.
  if (left <= -DAY) return "closed";
  if (left <= 0) return "overtime";
  if (left <= HOUR) return "final";
  if (left <= DAY) return "closing";
  return "upcoming";
}

/** When the daily refresh runs. Lots are checked once a day at this local time, and otherwise only on request. */
export const DAILY_REFRESH = { hour: 12, timeZone: "America/New_York" } as const;

/** A price from the latest daily run is current; this allows a full day plus time for the run itself. */
export const FRESH_FOR_MS = 25 * HOUR;

/** Milliseconds to add to UTC to get local wall-clock time in `timeZone` at instant `t`. */
function zoneOffset(t: number, timeZone: string): number {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", { timeZone, hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric" })
      .formatToParts(new Date(t))
      .map((x) => [x.type, Number(x.value)]),
  ) as Record<string, number>;
  return Date.UTC(p.year!, p.month! - 1, p.day!, p.hour!, p.minute!, p.second!) - Math.floor(t / 1000) * 1000;
}

/** The instant of hour:00 local time on the local calendar day containing `t` (DST-safe). */
function localHourOn(t: number, hour: number, timeZone: string): number {
  const local = new Date(t + zoneOffset(t, timeZone));
  const guess = Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate(), hour);
  const first = guess - zoneOffset(guess, timeZone);
  return guess - zoneOffset(first, timeZone);
}

/** The most recent daily refresh time at or before `now`. */
export function lastDailyRun(now: number, at: { hour: number; timeZone: string } = DAILY_REFRESH): number {
  const today = localHourOn(now, at.hour, at.timeZone);
  return today <= now ? today : localHourOn(now - DAY, at.hour, at.timeZone);
}

/** The next daily refresh time strictly after `now`. */
export function nextDailyRun(now: number, at: { hour: number; timeZone: string } = DAILY_REFRESH): number {
  const today = localHourOn(now, at.hour, at.timeZone);
  return today > now ? today : localHourOn(now + DAY, at.hour, at.timeZone);
}

/** fresh: within budget. stale: up to 2× (missed one daily run). failing: beyond that. */
export function freshnessLevel(lastOkAt: string | null, budgetMs: number | null, now: number) {
  if (!lastOkAt) return { level: "none" as const, ageMs: null };
  const ageMs = Math.max(0, now - Date.parse(lastOkAt));
  if (budgetMs == null) return { level: "fresh" as const, ageMs };
  const ratio = ageMs / budgetMs;
  return { level: ratio <= 1 ? ("fresh" as const) : ratio <= 2 ? ("stale" as const) : ("failing" as const), ageMs };
}
